<!-- Constatering.vue -->
<template>
  <div
    class="border-background-300 relative rounded-sm border p-3 transition-colors duration-300"
    :class="{
      'border-amber-400 shadow-[0_0_0_1px_#fbbf24]': hasUnsavedChanges && !saveError,
      'border-red-400 shadow-[0_0_0_1px_#f87171]': !!saveError,
      'border-red-300 bg-red-50/30': modelValue.herstelStatus === 'rejected' && !saveError,
    }"
  >
    <!-- Unsaved changes indicator -->
    <span v-if="hasUnsavedChanges && !saveError" class="absolute top-1.5 right-1.5 text-sm text-amber-400">
      &#9679;
    </span>

    <div class="border-background-200 mb-3 flex justify-between border-b pb-2">
      <div class="flex items-center gap-2 self-center">
        <span
          v-if="modelValue.volgnummer"
          class="border-background-300 text-background-500 border-r pr-2 text-xs font-semibold"
          >#{{ modelValue.volgnummer }}</span
        >
        <!-- Status text -->
        <span v-if="modelValue.herstelStatus === 'rejected'" class="text-sm font-medium text-wrap text-red-600"
          >Afgekeurd</span
        >
        <span v-else-if="!modelValue.hersteld" class="text-background-600 text-sm text-wrap"
          >Te herstellen</span
        >
        <div v-else-if="modelValue.hersteld && modelValue.herstelStatus !== 'accepted'" class="flex items-center gap-2">
          <AppIcon name="clock" class="text-background-500" />
          <span class="text-background-600 text-sm text-wrap">Wachten op inspecteur</span>
        </div>
        <div v-else-if="modelValue.herstelStatus === 'accepted'" class="flex items-center gap-2">
          <AppIcon name="check" class="text-emerald-500" />
          <span class="text-background-600 text-sm text-wrap">Hersteld en bevestigd</span>
        </div>
      </div>
      <div class="ml-auto flex items-center justify-end gap-2 text-xs">
        <Transition name="fade" mode="out-in">
          <span
            :key="String(modelValue.hersteld)"
            class="flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white"
            :class="modelValue.hersteld ? 'bg-emerald-500' : 'bg-red-400'"
            :title="modelValue.hersteld ? 'Hersteld' : 'Niet hersteld'"
          >
            {{ modelValue.hersteld ? "&#10003;" : "&#10005;" }}
          </span>
        </Transition>
        <Transition name="fade" mode="out-in">
          <span
            :key="String(modelValue.herstelStatus)"
            class="flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white"
            :class="
              modelValue.herstelStatus === 'accepted'
                ? 'bg-emerald-500'
                : modelValue.herstelStatus === 'rejected'
                  ? 'bg-red-500'
                  : 'bg-background-300'
            "
            :title="
              modelValue.herstelStatus === 'accepted'
                ? 'Bevestigd door inspecteur'
                : modelValue.herstelStatus === 'rejected'
                  ? 'Afgekeurd door inspecteur'
                  : 'Niet bevestigd'
            "
          >
            {{ modelValue.herstelStatus === 'accepted' ? "&#10003;" : "&#10005;" }}
          </span>
        </Transition>
      </div>
    </div>

    <!-- Rejection banner -->
    <div
      v-if="modelValue.herstelStatus === 'rejected' && lastRejectionMessage"
      class="mb-3 rounded-sm border border-red-200 bg-red-100 p-3"
    >
      <div class="flex items-start gap-2">
        <i class="pi pi-exclamation-triangle mt-0.5 text-red-600"></i>
        <div class="flex-1">
          <span class="text-sm font-semibold text-red-800">Afgekeurd door inspecteur</span>
          <p class="mt-1 text-sm whitespace-pre-wrap text-red-700 italic">"{{ lastRejectionMessage.message }}"</p>
          <span class="mt-1 block text-xs text-red-500">{{ formatRelativeTime(lastRejectionMessage.sent_at) }}</span>
        </div>
      </div>
    </div>

    <!-- Photo Gallery Grid -->
    <div class="mb-2 grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-2">
      <!-- All original photos -->
      <div v-for="foto in originalPhotos" :key="foto.id" class="flex flex-col gap-1">
        <span class="text-background-500 truncate text-xs font-medium">{{ foto.label }}</span>
        <div class="bg-background-100 relative aspect-square overflow-hidden rounded-md">
          <img
            :ref="(el) => setThumbRef(foto.id, el as HTMLElement | null)"
            :src="foto.thumbnailUrl"
            :alt="foto.label"
            class="h-full w-full cursor-pointer object-cover transition-transform duration-200 hover:scale-105"
            loading="lazy"
            @click="openOriginalPhoto(foto.id)"
          />
        </div>
      </div>

      <!-- Herstel Photos -->
      <div v-for="photo in allHerstelPhotos" :key="photo.id" class="flex flex-col gap-1">
        <span class="truncate text-xs font-bold" :class="photo.isTemp ? 'text-orange-600' : 'text-green-800'">{{
          photo.isTemp ? "Nieuw" : "Hersteld"
        }}</span>
        <div class="bg-background-100 relative aspect-square overflow-hidden rounded-md">
          <img
            :ref="(el) => setThumbRef(`herstel-${photo.id}`, el as HTMLElement | null)"
            :src="photo.thumbnailUrl"
            alt="Herstelde foto"
            class="h-full w-full cursor-pointer object-cover transition-transform duration-200 hover:scale-105"
            loading="lazy"
            @click="!photo.isTemp && openHerstelPhoto(photo.id)"
          />
          <!-- Delete button (edit mode only) -->
          <button
            v-if="isHerstellen"
            class="absolute top-1 right-1 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-red-500 text-white shadow-sm"
            @click.stop="photo.isTemp ? deleteTempPhoto(photo.id) : deleteExistingPhoto(photo.id)"
          >
            <i class="pi pi-times" style="font-size: 0.55rem" />
          </button>
        </div>
      </div>

      <!-- Add photo button (edit mode only) -->
      <div v-if="isHerstellen" class="flex flex-col gap-1">
        <span class="text-background-500 text-xs font-medium">Toevoegen</span>
        <div
          class="border-background-300 bg-background-50 relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-md border-2 border-dashed"
        >
          <input
            type="file"
            accept="image/*"
            class="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            @change="handlePhotoUpload"
          />
          <div class="text-background-400 flex flex-col items-center text-xs">
            <i class="pi pi-camera mb-1 text-lg" />
            <span>{{ isUploading ? "Uploading..." : "Upload" }}</span>
          </div>
        </div>
      </div>

      <!-- Placeholder when no photos and not editing -->
      <div v-if="!hasAnyHerstelPhoto && !isHerstellen" class="flex flex-col gap-1">
        <span class="text-background-500 text-xs font-medium">Hersteld</span>
        <div
          class="bg-background-100 text-background-400 flex aspect-square items-center justify-center overflow-hidden rounded-md"
        >
          <i class="pi pi-image text-2xl" />
        </div>
      </div>
    </div>

    <!-- Beoordeling + Status Row (inline) -->
    <div class="mb-2 flex flex-wrap items-center gap-2">
      <div
        class="rounded px-3 py-1 text-xs font-bold tracking-wider transition-all duration-300"
        :class="assessmentClass"
      >
        {{
          modelValue.beoordeling
            ? modelValue.beoordeling.charAt(0).toUpperCase() + modelValue.beoordeling.slice(1)
            : "Geen"
        }}
      </div>
      <span
        v-if="modelValue.meerdere_locaties"
        class="border-background-300 text-background-600 rounded-full border px-2 py-0.5 text-xs font-medium"
      >
        Meerdere locaties
      </span>
    </div>

    <!-- Opmerkingen -->
    <span class="font-semibold">Opmerking inspecteur</span>
    <span v-if="modelValue.opmerkingen" class="text-background-700 mb-2 block text-sm whitespace-pre-wrap">
      {{ modelValue.opmerkingen }}
    </span>
    <!-- Recorded once by the inspecteur, but present in more places: the
         herstel must cover every comparable situation, not only the one shown. -->
    <div
      v-if="modelValue.meerdere_locaties"
      class="mb-2 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
    >
      <i class="pi pi-map-marker mt-0.5 text-amber-600" />
      <span>
        Deze constatering komt ook op andere locaties in de installatie voor. De genoemde locatie en foto's zijn een
        voorbeeld; herstel alle vergelijkbare situaties.
      </span>
    </div>

    <InputField
      v-if="isHerstellen"
      :value="localHOpmerking"
      label="Opmerking installateur"
      :editable="true"
      :textarea="true"
      placeholder="Beschrijf de uitgevoerde herstelwerkzaamheden"
      @update:modelValue="localHOpmerking = $event"
      :id="`constatering-${modelValue.id}-opmerking-installateur`"
    />
    <p v-if="isHerstellen && !localHOpmerking.trim()" class="mt-1 text-xs text-red-600">
      Opmerking installateur is verplicht.
    </p>
    <p v-if="isHerstellen && herstelPhotos.length === 0 && tempPhotos.length === 0" class="mt-1 text-xs text-red-600">
      Minimaal 1 herstelfoto is verplicht.
    </p>

    <!-- Revision explanation (only when revising after rejection) -->
    <div v-if="isHerstellen && modelValue.herstelStatus === 'rejected'" class="mt-2">
      <label class="text-background-700 mb-1 block text-sm font-semibold">Toelichting revisie</label>
      <textarea
        v-model="revisionMessage"
        class="border-background-300 w-full rounded-sm border p-2 text-sm"
        rows="2"
        placeholder="Beschrijf wat er is aangepast..."
      ></textarea>
    </div>

    <!-- Conversation timeline -->
    <div v-if="herstelCommunicatie.length > 0" class="mt-3">
      <button
        class="text-background-500 hover:text-background-700 mb-2 flex cursor-pointer items-center gap-1 text-xs font-semibold"
        @click="showTimeline = !showTimeline"
      >
        <i class="pi" :class="showTimeline ? 'pi-chevron-down' : 'pi-chevron-right'" style="font-size: 0.65rem"></i>
        Communicatie ({{ herstelCommunicatie.length }})
      </button>
      <div v-if="showTimeline" class="space-y-2">
        <div
          v-for="(bericht, idx) in herstelCommunicatie"
          :key="idx"
          class="border-background-200 rounded-sm border p-2"
          :class="{
            'border-l-2 border-l-red-400': bericht.type === 'rejection',
            'border-l-2 border-l-green-400': bericht.type === 'acceptance',
            'border-l-2 border-l-blue-400': bericht.type === 'revision',
          }"
        >
          <div class="mb-1 flex items-center gap-2">
            <span
              class="rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
              :class="
                bericht.sender_role === 'inspecteur' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
              "
            >
              {{ bericht.sender_role === "inspecteur" ? "Inspecteur" : "Installateur" }}
            </span>
            <span class="text-background-400 text-[10px]">{{ formatRelativeTime(bericht.sent_at) }}</span>
          </div>
          <p class="text-background-700 text-xs whitespace-pre-wrap">{{ bericht.message }}</p>
        </div>
      </div>
    </div>

    <!-- Persistent save error -->
    <div
      v-if="saveError"
      class="mt-2 flex items-start gap-2 rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700"
    >
      <i class="pi pi-exclamation-triangle mt-0.5"></i>
      <span>{{ saveError }}</span>
    </div>

    <!-- Actions -->
    <div class="mt-2 pt-2">
      <div class="flex justify-end gap-2">
        <Button v-if="showHerstelButton" label="Herstellen" size="small" @click="startHerstellen" />
        <Button v-if="showEditButton" label="Aanpassen" severity="secondary" size="small" @click="startHerstellen" />
        <Button
          v-if="showRevisionButton"
          label="Opnieuw indienen"
          severity="warn"
          size="small"
          @click="startHerstellen"
        />
        <template v-if="isHerstellen">
          <Button
            label="Annuleren"
            severity="secondary"
            outlined
            size="small"
            :disabled="isSaving"
            @click="cancelHerstellen"
          />
          <Button
            label="Opslaan"
            size="small"
            :loading="isSaving"
            :disabled="!canSaveHerstellen"
            @click="saveHerstellen"
          />
        </template>
      </div>
    </div>

    <!-- Image Crop Dialog -->
    <ImageCropDialog
      v-model:visible="showCropDialog"
      :image-url="cropImageUrl"
      @crop="handleCroppedImage"
      @update:visible="handleCropCancel"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onUnmounted, watch, watchEffect, nextTick } from "vue";
  import Button from "primevue/button";
  import { supabase } from "../lib/supabase";
  import { resolveStorageUrl } from "../utils/storageUrl";
  import { useToast } from "../composables/useToast";
  import { useConfirm } from "primevue/useconfirm";
  import AppIcon from "./AppIcon.vue";
  import {
    registerGalleryImage,
    unregisterGalleryImagesByPrefix,
    openGalleryAt,
    updateGalleryThumbEl,
  } from "../composables/usePhotoSwipeGallery";
  import { useRapportenStore } from "../stores/rapporten";
  import InputField from "./InputField.vue";
  import ImageCropDialog from "./ImageCropDialog.vue";
  import type { HerstelBericht } from "../../../../types/shared/common";

  const props = defineProps({
    modelValue: {
      type: Object,
      required: true,
      default: () => ({
        hersteld: false,
        hFoto: null,
        herstelStatus: null,
        herstelCommunicatie: [],
      }),
    },
    rapportNr: {
      type: String,
      required: true,
    },
    rapportId: {
      type: String,
      required: true,
    },
  });

  type DirtyChangePayload = { id: string; volgnummer: number | null; dirty: boolean; canSave: boolean };
  const emit = defineEmits<{
    "update:modelValue": [value: Record<string, any>];
    "dirty-change": [payload: DirtyChangePayload];
  }>();
  const toast = useToast();
  const confirm = useConfirm();
  const rapportenStore = useRapportenStore();

  const isHerstellen = ref(false);
  const isSaving = ref(false);
  const localHOpmerking = ref("");
  const isUploading = ref(false);
  const revisionMessage = ref("");
  const showTimeline = ref(false);
  // Comment the editor opened with, so we can detect genuine edits.
  const editBaselineComment = ref("");
  // Persistent error shown on the card when a save fails (toast alone is transient).
  const saveError = ref<string | null>(null);

  interface TempHerstelPhoto {
    id: string;
    file: File;
    previewUrl: string;
  }
  const tempPhotos = ref<TempHerstelPhoto[]>([]);
  const deletedFotoIds = ref<string[]>([]);

  const isOriginalFotoLabel = (label: unknown): boolean =>
    typeof label === "string" && label.startsWith("constatering_foto");
  const isHerstelFotoLabel = (label: unknown): boolean =>
    typeof label === "string" && label.startsWith("constatering_herstel");

  // Crop dialog state
  const showCropDialog = ref(false);
  const cropImageUrl = ref("");
  const pendingCropFile = ref<File | null>(null);

  // Dynamic thumbnail refs for PhotoSwipe zoom animation
  const thumbRefs = ref<{ [key: string]: HTMLElement | null }>({});
  const setThumbRef = (id: string, el: HTMLElement | null) => {
    if (el) {
      thumbRefs.value[id] = el;
    }
  };

  // Parsed herstel communicatie
  const herstelCommunicatie = computed<HerstelBericht[]>(() => {
    const comm = props.modelValue.herstelCommunicatie;
    if (!comm) return [];
    if (Array.isArray(comm)) return comm;
    if (typeof comm === "string") {
      try {
        const parsed = JSON.parse(comm);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  // Last rejection message for the banner
  const lastRejectionMessage = computed<HerstelBericht | null>(() => {
    const rejections = herstelCommunicatie.value.filter((b) => b.type === "rejection");
    return rejections.length > 0 ? rejections[rejections.length - 1] : null;
  });

  function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Zojuist";
    if (diffMins < 60) return `${diffMins} min geleden`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} uur geleden`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} dag${diffDays > 1 ? "en" : ""} geleden`;
    return date.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
  }

  // ── Image URL Helpers ──────────────────────────────────────────────

  // Plain storage object URLs, deliberately without Supabase image
  // transformations (render/image): every distinct origin image transformed
  // in a billing period is billed, and the herstel page was the only place
  // requesting them. Cache-bust on lastUpdated so a re-uploaded photo shows.
  const getStorageImageUrl = (url = "") => {
    if (!url) return "";
    const resolvedUrl = resolveStorageUrl(url);

    const lastUpdated = props.modelValue.lastUpdated;
    const cacheBust =
      typeof lastUpdated === "number" ? lastUpdated : typeof lastUpdated === "string" ? Date.parse(lastUpdated) : NaN;
    if (!Number.isFinite(cacheBust)) return resolvedUrl;

    const separator = resolvedUrl.includes("?") ? "&" : "?";
    return `${resolvedUrl}${separator}t=${cacheBust}`;
  };

  // ── Gallery Photos (multiple originals + herstelde) ────────────────

  const originalPhotos = computed(() => {
    const fotos: { id: string; label?: string; url: string | null }[] = props.modelValue.fotos || [];
    const original = fotos.filter((f: any) => isOriginalFotoLabel(f?.label) && !!f?.url);

    return original.map((f, index: number) => {
      const url = f.url as string;
      return {
        id: f.id,
        url,
        label: `Originele foto ${original.length > 1 ? index + 1 : ""}`.trim(),
        thumbnailUrl: getStorageImageUrl(url),
      };
    });
  });

  const herstelPhotos = computed(() => {
    const fotos: { id: string; label?: string; url: string | null }[] = props.modelValue.fotos || [];
    return fotos
      .filter((f: any) => isHerstelFotoLabel(f?.label) && !!f?.url)
      .filter((f: any) => !deletedFotoIds.value.includes(f.id));
  });

  const allHerstelPhotos = computed(() => {
    const persisted = herstelPhotos.value.map((f) => ({
      id: f.id,
      url: f.url as string,
      label: f.label,
      isTemp: false,
      thumbnailUrl: getStorageImageUrl(f.url as string),
    }));
    const temp = tempPhotos.value.map((t) => ({
      id: t.id,
      url: t.previewUrl,
      label: "Nieuwe foto",
      isTemp: true,
      thumbnailUrl: t.previewUrl,
    }));
    return [...persisted, ...temp];
  });

  const hasAnyHerstelPhoto = computed(() => allHerstelPhotos.value.length > 0);

  // ── PhotoSwipe Gallery ─────────────────────────────────────────────

  const pswpGalleryId = `constatering-${props.modelValue.id}`;

  watchEffect(() => {
    unregisterGalleryImagesByPrefix(pswpGalleryId, "original-");
    for (const photo of originalPhotos.value) {
      registerGalleryImage({
        galleryId: pswpGalleryId,
        imageId: `original-${photo.id}`,
        src: photo.url,
        label: photo.label,
        thumbEl: thumbRefs.value[photo.id] || undefined,
        thumbSrc: photo.thumbnailUrl,
      });
    }
  });

  watchEffect(() => {
    unregisterGalleryImagesByPrefix(pswpGalleryId, "herstelde-");
    for (const photo of allHerstelPhotos.value) {
      if (!photo.isTemp) {
        registerGalleryImage({
          galleryId: pswpGalleryId,
          imageId: `herstelde-${photo.id}`,
          src: photo.url,
          label: "Herstelde foto",
          thumbEl: thumbRefs.value[`herstel-${photo.id}`] || undefined,
          thumbSrc: photo.thumbnailUrl,
        });
      }
    }
  });

  watch(
    thumbRefs,
    () => {
      nextTick(() => {
        for (const photo of originalPhotos.value) {
          const el = thumbRefs.value[photo.id];
          if (el) updateGalleryThumbEl(pswpGalleryId, `original-${photo.id}`, el);
        }
        for (const photo of allHerstelPhotos.value) {
          if (!photo.isTemp) {
            const el = thumbRefs.value[`herstel-${photo.id}`];
            if (el) updateGalleryThumbEl(pswpGalleryId, `herstelde-${photo.id}`, el);
          }
        }
      });
    },
    { deep: true },
  );

  const openOriginalPhoto = (photoId: string) => {
    openGalleryAt(pswpGalleryId, `original-${photoId}`);
  };

  const openHerstelPhoto = (photoId: string) => {
    openGalleryAt(pswpGalleryId, `herstelde-${photoId}`);
  };

  onUnmounted(() => {
    unregisterGalleryImagesByPrefix(pswpGalleryId, "original-");
    unregisterGalleryImagesByPrefix(pswpGalleryId, "herstelde-");
  });

  // ── Assessment styling ─────────────────────────────────────────────

  const assessmentClass = computed(() => {
    const b = props.modelValue.beoordeling as string | undefined;
    if (!b) return "bg-background-300 text-background-600";
    const map: Record<string, string> = {
      ernstig: "bg-[var(--color-ernstig)] text-white",
      serieus: "bg-[var(--color-serieus)] text-black",
      gering: "bg-[var(--color-gering)] text-black",
      opmerking: "bg-[var(--color-opmerking)] text-white",
    };
    return map[b] || "bg-background-300 text-background-600";
  });

  // ── Business Logic ─────────────────────────────────────

  const hasUnsavedChanges = computed(() => {
    return tempPhotos.value.length > 0 || deletedFotoIds.value.length > 0;
  });

  const showHerstelButton = computed(
    () => !props.modelValue?.hersteld && !isHerstellen.value && props.modelValue.herstelStatus !== "rejected",
  );
  const showEditButton = computed(
    () =>
      props.modelValue?.hersteld &&
      props.modelValue?.herstelStatus !== "accepted" &&
      !isHerstellen.value &&
      props.modelValue.herstelStatus !== "rejected",
  );
  const showRevisionButton = computed(() => props.modelValue.herstelStatus === "rejected" && !isHerstellen.value);
  const canSaveHerstellen = computed(
    () =>
      localHOpmerking.value.trim().length > 0 &&
      !isSaving.value &&
      (herstelPhotos.value.length > 0 || tempPhotos.value.length > 0),
  );

  // A card is "dirty" when it is open for editing and holds input that would be
  // lost on refresh/navigation if not saved (photos, deletions, or comment changes).
  const isDirty = computed(() => {
    if (!isHerstellen.value) return false;
    return (
      tempPhotos.value.length > 0 ||
      deletedFotoIds.value.length > 0 ||
      localHOpmerking.value.trim() !== editBaselineComment.value.trim() ||
      revisionMessage.value.trim().length > 0
    );
  });

  // Report dirty state up to the parent so it can warn before leaving and flush
  // pending saves before submitting the herstelverklaring.
  watchEffect(() => {
    emit("dirty-change", {
      id: props.modelValue.id,
      volgnummer: props.modelValue.volgnummer ?? null,
      dirty: isDirty.value,
      canSave: canSaveHerstellen.value,
    });
  });
  const lastInstallerSubmission = computed<HerstelBericht | null>(() => {
    for (let index = herstelCommunicatie.value.length - 1; index >= 0; index -= 1) {
      const message = herstelCommunicatie.value[index];
      if (message.sender_role === "installateur" && (message.type === "herstel" || message.type === "revision")) {
        return message;
      }
    }

    return null;
  });

  const startHerstellen = () => {
    isHerstellen.value = true;
    localHOpmerking.value =
      props.modelValue.herstelStatus === "rejected" ? "" : lastInstallerSubmission.value?.message || "";
    editBaselineComment.value = localHOpmerking.value;
    revisionMessage.value = "";
    saveError.value = null;
  };

  const cancelHerstellen = () => {
    clearTempImages();
    localHOpmerking.value = "";
    editBaselineComment.value = "";
    revisionMessage.value = "";
    saveError.value = null;
    isHerstellen.value = false;
  };

  const clearTempImages = () => {
    for (const t of tempPhotos.value) {
      URL.revokeObjectURL(t.previewUrl);
    }
    tempPhotos.value = [];
    deletedFotoIds.value = [];
  };

  const deleteExistingPhoto = (fotoId: string) => {
    confirm.require({
      message:
        "Weet u zeker dat u deze herstelfoto wilt verwijderen? " +
        "De foto wordt definitief verwijderd bij het opslaan.",
      header: "Foto verwijderen",
      icon: "pi pi-exclamation-triangle",
      rejectLabel: "Annuleren",
      acceptLabel: "Verwijderen",
      acceptClass: "p-button-danger",
      accept: () => {
        deletedFotoIds.value.push(fotoId);
      },
    });
  };

  const deleteTempPhoto = (tempId: string) => {
    const idx = tempPhotos.value.findIndex((t) => t.id === tempId);
    if (idx !== -1) {
      URL.revokeObjectURL(tempPhotos.value[idx].previewUrl);
      tempPhotos.value.splice(idx, 1);
    }
  };

  // ── JPEG Conversion ────────────────────────────────────────────────

  const convertToJpeg = async (file: File): Promise<File> => {
    return new Promise<File>((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Failed to get canvas context"));
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              reject(new Error("Failed to convert image to JPEG"));
              return;
            }
            const jpegFile = new File([blob], `${file.name.split(".")[0]}.jpg`, {
              type: "image/jpeg",
            });
            resolve(jpegFile);
          },
          "image/jpeg",
          0.8,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image"));
      };
      img.src = objectUrl;
    });
  };

  // ── Image Dimension Check (for crop) ───────────────────────────────

  const checkImageDimensions = (
    file: File,
    tolerance = 10,
  ): Promise<{ width: number; height: number; isSquare: boolean }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const diff = Math.abs(img.width - img.height);
        resolve({ width: img.width, height: img.height, isSquare: diff < tolerance });
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(objectUrl);
        reject(e);
      };
      img.src = objectUrl;
    });
  };

  // ── Photo Upload with Crop ─────────────────────────────────────────

  const addTempPhoto = (file: File) => {
    tempPhotos.value.push({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    });
  };

  const handlePhotoUpload = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.item(0);
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Bestand is te groot. Maximum grootte is 10MB", undefined, { report: false });
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Ongeldig bestandstype. Gebruik JPG, PNG, GIF of WebP", undefined, { report: false });
      return;
    }

    try {
      const { isSquare } = await checkImageDimensions(file);
      if (!isSquare) {
        pendingCropFile.value = file;
        cropImageUrl.value = URL.createObjectURL(file);
        showCropDialog.value = true;
        input.value = "";
        return;
      }
      addTempPhoto(file);
    } catch (error) {
      console.error("Error checking image dimensions:", error);
      addTempPhoto(file);
    }

    input.value = "";
  };

  const handleCroppedImage = (result: { file: File }) => {
    showCropDialog.value = false;
    if (cropImageUrl.value) {
      URL.revokeObjectURL(cropImageUrl.value);
      cropImageUrl.value = "";
    }
    pendingCropFile.value = null;
    addTempPhoto(result.file);
  };

  const handleCropCancel = (val: boolean) => {
    if (!val) {
      showCropDialog.value = false;
      if (cropImageUrl.value) {
        URL.revokeObjectURL(cropImageUrl.value);
        cropImageUrl.value = "";
      }
      pendingCropFile.value = null;
    }
  };

  // ── Get current user ID ──────────────────────────────────────────

  async function getCurrentUserId(): Promise<string> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user?.id || "";
  }

  function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === "object" && error !== null && "message" in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) {
        return message;
      }
    }

    if (typeof error === "object" && error !== null) {
      const supabaseError = error as {
        code?: unknown;
        details?: unknown;
        hint?: unknown;
        error_description?: unknown;
      };

      const parts = [supabaseError.code, supabaseError.details, supabaseError.hint, supabaseError.error_description]
        .filter((part): part is string => typeof part === "string" && part.trim().length > 0)
        .map((part) => part.trim());

      if (parts.length > 0) {
        return parts.join(" | ");
      }

      try {
        return JSON.stringify(error);
      } catch {
        // Fall through to String(error)
      }
    }

    return String(error);
  }

  // ── Save Logic ─────────────────────────────────────────

  const saveHerstellen = async (): Promise<boolean> => {
    if (!canSaveHerstellen.value) {
      toast.error("Opmerking installateur en minimaal 1 foto zijn verplicht");
      return false;
    }

    saveError.value = null;
    isSaving.value = true;
    isUploading.value = true;

    try {
      // 1. Delete marked photos from DB (storage cleaned by trigger)
      for (const fotoId of deletedFotoIds.value) {
        const { error } = await supabase.schema("rapport_details").from("fotos").delete().eq("id", fotoId);
        if (error) throw error;
      }

      // 2. Upload and insert new temp photos
      interface UploadedPhoto {
        fotoId: string;
        filePath: string;
        contextLabel: string;
      }
      const uploadedPhotos: UploadedPhoto[] = [];
      const baseSortOrder = herstelPhotos.value.length;

      for (let i = 0; i < tempPhotos.value.length; i++) {
        const temp = tempPhotos.value[i];
        const jpegFile = await convertToJpeg(temp.file);

        const uniqueId = crypto.randomUUID().slice(0, 8);
        const fileName = `constatering_${props.modelValue.id}_herstel_${uniqueId}.jpg`;
        const filePath = `rapporten/${props.rapportId}/constateringen/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("userFiles")
          .upload(filePath, jpegFile, { cacheControl: "0" });
        if (uploadError) throw uploadError;

        const contextLabel = `constatering_herstel_${props.modelValue.id}_${uniqueId}`;
        const { data: insertedFoto, error: insertError } = await supabase
          .schema("rapport_details")
          .from("fotos")
          .insert({
            rapport_id: props.rapportId,
            constatering_id: props.modelValue.id,
            context_label: contextLabel,
            remote_url: filePath,
            mime_type: "image/jpeg",
            sort_order: baseSortOrder + i,
          })
          .select("id")
          .single();
        if (insertError) throw insertError;

        uploadedPhotos.push({
          fotoId: insertedFoto.id,
          filePath,
          contextLabel,
        });
      }

      isUploading.value = false;

      // 3. Compute hersteld status
      const remainingPersistedIds = herstelPhotos.value.map((f) => f.id);
      const newFotoIds = uploadedPhotos.map((p) => p.fotoId);
      const allFotoIds = [...remainingPersistedIds, ...newFotoIds];
      const newHOpmerking = localHOpmerking.value;
      const hasValidPhoto = allFotoIds.length > 0;
      const hasValidComment = !!newHOpmerking?.trim();
      const hersteld = hasValidPhoto && hasValidComment;

      // 4. Build herstel_communicatie update
      const isRevision = props.modelValue.herstelStatus === "rejected";
      let updatedCommunicatie = [...herstelCommunicatie.value];
      let communicatieChanged = false;
      const lastMessage = updatedCommunicatie[updatedCommunicatie.length - 1] || null;

      if (isRevision && revisionMessage.value.trim()) {
        const userId = await getCurrentUserId();
        const newMessage: HerstelBericht = {
          type: "revision",
          sender_id: userId,
          sender_role: "installateur",
          message: revisionMessage.value.trim(),
          foto_ids: allFotoIds,
          sent_at: new Date().toISOString(),
        };
        updatedCommunicatie = [...updatedCommunicatie, newMessage];
        communicatieChanged = true;
      } else if (!isRevision && newHOpmerking?.trim()) {
        const canEditLastSubmission =
          lastMessage?.sender_role === "installateur" &&
          (lastMessage.type === "herstel" || lastMessage.type === "revision");

        if (canEditLastSubmission) {
          updatedCommunicatie[updatedCommunicatie.length - 1] = {
            ...lastMessage,
            message: newHOpmerking.trim(),
            foto_ids: allFotoIds,
            sent_at: new Date().toISOString(),
          };
        } else {
          const userId = await getCurrentUserId();
          const newMessage: HerstelBericht = {
            type: "herstel",
            sender_id: userId,
            sender_role: "installateur",
            message: newHOpmerking.trim(),
            foto_ids: allFotoIds,
            sent_at: new Date().toISOString(),
          };
          updatedCommunicatie = [...updatedCommunicatie, newMessage];
        }
        communicatieChanged = true;
      }

      // 5. Determine new herstel_status
      const newHerstelStatus = hersteld ? "pending" : props.modelValue.herstelStatus;

      // 6. Update constateringen row in DB
      const updatePayload: Record<string, any> = { hersteld };
      if (newHerstelStatus) {
        updatePayload.herstel_status = newHerstelStatus;
      }
      if (communicatieChanged) {
        updatePayload.herstel_communicatie = updatedCommunicatie;
      }

      rapportenStore.suppressRealtimeRefetch(props.rapportId);

      const { error: updateError } = await supabase
        .schema("rapport_details")
        .from("constateringen")
        .update(updatePayload)
        .eq("id", props.modelValue.id);
      if (updateError) throw updateError;

      // 7. Build updated fotos array for local state emit
      let updatedFotos = (props.modelValue.fotos || []).filter((f: any) => !deletedFotoIds.value.includes(f.id));
      for (const uploaded of uploadedPhotos) {
        updatedFotos.push({
          id: uploaded.fotoId,
          url: resolveStorageUrl(uploaded.filePath),
          label: uploaded.contextLabel,
        });
      }

      const photosChanged = deletedFotoIds.value.length > 0 || uploadedPhotos.length > 0;

      emit("update:modelValue", {
        ...props.modelValue,
        hersteld,
        herstelStatus: newHerstelStatus || props.modelValue.herstelStatus,
        herstelCommunicatie: updatedCommunicatie,
        lastUpdated: photosChanged ? new Date().toISOString() : props.modelValue.lastUpdated,
        fotos: updatedFotos,
      });

      clearTempImages();
      revisionMessage.value = "";
      editBaselineComment.value = "";
      saveError.value = null;
      isHerstellen.value = false;
      toast.success("Wijzigingen succesvol opgeslagen");
      return true;
    } catch (error) {
      console.error("[saveHerstellen] ERROR:", error);
      const message = getErrorMessage(error);
      saveError.value = `Opslaan mislukt: ${message}. Probeer het opnieuw.`;
      toast.error(`Er is een fout opgetreden bij het opslaan: ${message}`, error);
      return false;
    } finally {
      isSaving.value = false;
      isUploading.value = false;
    }
  };

  // Called by the parent before submitting the herstelverklaring: persists this
  // card if it has unsaved, valid changes. Returns a status the parent can act on.
  const flushSave = async (): Promise<{
    id: string;
    volgnummer: number | null;
    status: "clean" | "invalid" | "saved" | "error";
  }> => {
    const id = props.modelValue.id;
    const volgnummer = props.modelValue.volgnummer ?? null;
    if (!isDirty.value) return { id, volgnummer, status: "clean" };
    if (!canSaveHerstellen.value) {
      saveError.value = "Voeg een opmerking én minimaal 1 herstelfoto toe voordat u indient.";
      return { id, volgnummer, status: "invalid" };
    }
    const ok = await saveHerstellen();
    return { id, volgnummer, status: ok ? "saved" : "error" };
  };

  defineExpose({ flushSave });

  // ── Lifecycle ──────────────────────────────────────────────────────

  onUnmounted(() => {
    clearTempImages();
    if (cropImageUrl.value) {
      URL.revokeObjectURL(cropImageUrl.value);
    }
  });
</script>

<style scoped>
  /* Fade transition */
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(4px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }
</style>
